import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RegistrationChange } from "../../features/registrations/registrations-service";
import { RegistrationsViewModel } from "../../features/registrations/registrations-viewmodel";
import { UserIdViewModel } from "../../features/user-id/user-id-viewmodel";
import { AppRoutes } from "../../routes/routes";
import { formatTimestamp } from "../../utils/formatters";
import { useViewModel } from "../../viewmodel/use-view-model";
import { WriteAccessAction } from "../access/role-access";
import { Breadcrumb } from "../breadcrumb";
import { EmptyView } from "../empty/EmptyView";
import { RowKeyVal } from "../table/RowKeyVal";
import { bsClassForRegistrationStatus } from "./reg-bs-mapping";
import { RegistrationDetailChangeModalView } from "./registration-detail-change-modal-view";
import { RegistrationHistoryView } from "./registration-history-view";
export const RegistrationDetailView = observer(() => {
    const vm = useViewModel(RegistrationsViewModel);
    const userVM = useViewModel(UserIdViewModel);
    // const navigate = useNavigate();
    const { t } = useTranslation();
    const [showHistory, setShowHistory] = useState(false);
    // does not matter what changeType is default, since it will be changed before dialog opens
    const [changeType, setChangeType] = useState(RegistrationChange.block);
    const showHistoryClick = () => {
        if (!showHistory) {
            vm.getRegistrationHistory(vm.selectedRegistration?.registrationId);
        }
        setShowHistory(!showHistory);
    };
    // const showOperationsClick = () => {
    //     // navigate to operations with registration id filter filled in
    //     navigate(AppRoutes.Operations, { state: { filterRegId: vm.selectedRegistration?.registrationId } });
    // }
    const tHeaders = useMemo(() => ({
        name: t("registrations.detail.name"),
        status: t("registrations.detail.status"),
        application: t("registrations.detail.application"),
        platform: t("registrations.detail.platform"),
        created: t("registrations.detail.created"),
        lastUsed: t("registrations.detail.lastUsed"),
        deviceInfo: t("registrations.detail.deviceInfo"),
        reason: t("registrations.detail.reason"),
        blockExpire: t("registrations.detail.blockExpire"),
    }), [t]);
    if (vm.selectedRegistration == null) {
        return (_jsx(EmptyView, { message: _jsx(Trans, { i18nKey: "registrations.detail.notFound", children: _jsx(Link, { to: AppRoutes.Registrations }) }) }));
    }
    const onActionChange = async (change, userId, regId, externalId, blockReason) => {
        if (!regId) {
            console.log("no registration id");
            return;
        }
        await vm.changeRegistrationState(change, regId, userId, null, blockReason);
    };
    const classNameForChange = (changeType) => {
        switch (changeType) {
            case RegistrationChange.block:
                return "btn-warning";
            case RegistrationChange.unblock:
                return "btn-primary";
            case RegistrationChange.remove:
                return "btn-danger";
        }
    };
    const handleReloadRegistration = async () => await vm.reloadSelectedRegistration();
    const renderActions = (vm) => _jsx(_Fragment, { children: vm.availableRegistrationChanges.map((changeType) => (_jsx("span", { className: "ms-2", children: _jsx(WriteAccessAction, { children: _jsx("button", { type: "button", "data-bs-toggle": "modal", "data-bs-target": "#registrationChangeModal", className: `btn btn-sm ${classNameForChange(changeType)}`, onClick: () => setChangeType(changeType), children: t(`registrations.change.${changeType}`) }, changeType) }) }, changeType))) });
    return (_jsxs(_Fragment, { children: [_jsx(Breadcrumb, {}), _jsxs("div", { className: "card mb-3", children: [_jsxs("div", { className: "card-header pt-3 pb-3 d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsxs("span", { className: "fw-semibold userSelectDisabled", children: [t("registrations.detail.title"), ": "] }), _jsx("span", { className: "font-monospace userSelectEnabled", children: vm.selectedRegistration.registrationId })] }), _jsxs("button", { className: "btn btn-outline-secondary btn-sm", onClick: () => handleReloadRegistration(), children: [_jsx("i", { className: "bi bi-arrow-clockwise me-1" }), t("userTabs.reload")] })] }), _jsx("div", { className: "card-body", children: renderDetail(vm.selectedRegistration, tHeaders, t) }), _jsxs("div", { className: "card-footer d-flex justify-content-between", children: [_jsx("div", { children: _jsxs("button", { type: "button", className: "btn btn-secondary btn-sm me-2", onClick: showHistoryClick, children: [showHistory &&
                                            t("registrations.detail.historyHide"), !showHistory &&
                                            t("registrations.detail.historyShow")] }) }), _jsx("div", { className: "d-flex", children: renderActions(vm) })] })] }), vm.hasHistory && showHistory &&
                _jsx(RegistrationHistoryView, {}), _jsx(RegistrationDetailChangeModalView, { modalId: "registrationChangeModal", changeType: changeType, onConfirm: (reason) => void onActionChange(changeType, userVM.userId, vm.selectedRegistration?.registrationId, null, reason) })] }));
});
function renderDetail(reg, headers, t) {
    const statusElement = _jsx("span", { className: `badge ${bsClassForRegistrationStatus(reg)}`, children: t(`registrations.status.${reg.registrationStatus}`) });
    return _jsxs("div", { className: "card-text", children: [_jsx(RowKeyVal, { arr: [
                    { key: headers.name, val: reg.name },
                    { key: headers.status, elementVal: statusElement }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: headers.application, val: reg.applicationId },
                    { key: headers.platform, val: reg.platform }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: headers.created, val: formatTimestamp(reg.timestampCreated) },
                    { key: headers.deviceInfo, val: reg.deviceInfo }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: headers.lastUsed, val: formatTimestamp(reg.timestampLastUsed) },
                    ...(reg.registrationStatus === "BLOCKED"
                        ? [{ key: headers.reason, val: reg.blockedReason }]
                        : [])
                ] }), reg.registrationStatus === "BLOCKED" && reg.timestampBlockExpire != null &&
                _jsx(RowKeyVal, { arr: [
                        { key: headers.blockExpire, val: formatTimestamp(reg.timestampBlockExpire) }
                    ] })] });
}
